import React, { useState, useEffect, useCallback } from 'react';
import Select, { components } from 'react-select';
import AsyncSelect from 'react-select/async';
import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';

import { jsonContentStateFromString } from 'Shared/utils/draftJsHelpers';
import { searchTasks } from 'Shared/api/asana';
import { useAsanaData, useToggle } from 'Shared/utils/hooks';
import AsanaDots from 'Shared/images/AsanaDots.png';
import { WpModal, WpModalHeader, WpModalBody, WpModalFooter } from 'Wpds/components/modal';
import WpIcon from 'Wpds/components/icon/WpIcon';
import WpButton from 'Wpds/components/button/WpButton';
import WpButtonGroup from 'Wpds/components/button/WpButtonGroup';
import Colors from 'Wpds/colors/ColorConstants';

import './CreateItemFromAsanaModal.scss';
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';
import { useWorkspace } from 'Shared/utils/hooks/use-workspace';

type Props = {
  isOpen: boolean;
  onToggle: () => void;
  onAddMeetingItem: Function;
};

type OptionProps = {
  children: string;
  data: {
    projectName: string;
  };
};

const Option = (props: OptionProps) => {
  return (
    <components.Option {...props}>
      {props.children}
      <div>
        <small>{props.data.projectName}</small>
      </div>
    </components.Option>
  );
};

const CreateItemFromAsanaModal = ({ isOpen, onToggle, onAddMeetingItem }: Props) => {
  const [workspace, setWorkspace] = useState(null);
  const { workspace: wpWorkspace } = useWorkspace();
  const { user } = useCurrentUser();
  const [task, setTask] = useState(null);
  const [submitting, toggleSubmitting] = useToggle(false);
  const asanaData = useAsanaData();
  const workspaces = asanaData?.workspaces;
  const optionsForSelect = workspaces ? workspaces.map(({ id, name }) => ({ value: id, label: name })) : [];

  useEffect(() => {
    (window as any).analytics.track('Opened Modal', { modal: 'Import from Asana' });
  }, []);

  useEffect(() => {
    if (!workspace && optionsForSelect.length > 0) {
      setWorkspace(optionsForSelect[0]);
    }
  }, [workspace, optionsForSelect]);

  const handleWorkspaceChange = value => setWorkspace(value);

  const fetchTasks = (input, callback) => {
    searchTasks({ workspaceGid: workspace.value, query: input }).then(response => {
      callback(
        response.data.map(t => ({
          value: t.gid,
          label: t.name,
          projectId: t.project_gid,
          projectName: t.project_name,
          dueBy: t.due_by,
          assigneeEmail: t.assignee_email,
        }))
      );
    });
  };

  const debouncedFetchTasks = useCallback(debounce(fetchTasks, 500), [workspace]);

  const getTasks = (input, callback) => {
    if (isEmpty(input)) {
      return callback(null, { options: [] });
    }
    return debouncedFetchTasks(input, callback);
  };

  const handleTaskChange = option => {
    setTask(option);
  };

  const handleImportClick = () => {
    toggleSubmitting();
    const args = {
      jsonBody: jsonContentStateFromString(task.label),
      dueBy: task.dueBy,
      asanaData: {
        taskId: task.value,
        projectId: task.projectId,
        url: `http://app.asana.com/0/0/${task.value}`,
      },
      assignmentInfo: {},
      isAssignCurrentUser: true,
    };

    const assignedUser = wpWorkspace.users.find(u => u.email === task.assigneeEmail);
    if (assignedUser) {
      args.assignmentInfo = {
        assigneePersonId: assignedUser.personId,
        assigneeAvatar: assignedUser.avatar,
        assigneeName: assignedUser.fullName,
        assignerName: user.fullName,
        assignerUserId: user.id,
      };
      args.isAssignCurrentUser = false;
    } else {
      args.isAssignCurrentUser = false;
    }

    onAddMeetingItem(args);
    toggleSubmitting();
    onToggle();
  };

  return (
    <WpModal size="sm" isOpen={isOpen} onToggle={onToggle}>
      <WpModalHeader>
        Import from Asana
        <img className="CreateItemFromAsanaModal__logo ml-3" src={AsanaDots} alt="" />
      </WpModalHeader>
      <WpModalBody>
        <div className="CreateItemFromAsanaModal">
          <div className="mb-2">Choose the Asana task you would like to import.</div>
          <div className="CreateItemFromAsanaModal__label">Asana Workspace</div>
          <Select
            isSearchable
            options={optionsForSelect}
            placeholder="Choose Workspace"
            value={workspace}
            onChange={handleWorkspaceChange}
            menuPortalTarget={document.body}
            styles={{ menuPortal: base => ({ ...base, zIndex: '1050' }) }}
          />
          <div className="CreateItemFromAsanaModal__label mt-3">Search for a task</div>
          <AsyncSelect
            isSearchable
            defaultOptions
            loadOptions={getTasks}
            placeholder="Search..."
            value={task}
            onChange={handleTaskChange}
            noOptionsMessage={({ inputValue }) => (isEmpty(inputValue) ? null : 'No tasks found')}
            components={{
              Option,
              DropdownIndicator: () => (
                <span className="mr-2 pr-1">
                  <WpIcon name="search" color={Colors.gray[600]} />
                </span>
              ),
              IndicatorSeparator: () => null,
            }}
            menuPortalTarget={document.body}
            styles={{ menuPortal: base => ({ ...base, zIndex: '1050' }) }}
          />
          <div className="CreateItemFromAsanaModal__spacer" />
        </div>
      </WpModalBody>
      <WpModalFooter>
        <WpButtonGroup>
          <WpButton variant="tertiary" onClick={onToggle} disabled={submitting}>
            Cancel
          </WpButton>
          <WpButton disabled={!task || submitting} loading={submitting} onClick={handleImportClick}>
            Import
          </WpButton>
        </WpButtonGroup>
      </WpModalFooter>
    </WpModal>
  );
};

export default CreateItemFromAsanaModal;
